import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  Plus,
  TrendingUp,
  Activity,
  Users,
  Clock,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Trophy,
  Trash2,
  Edit,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  Flag,
  Gauge,
  BarChart3,
  GraduationCap,
  LineChart,
  Zap,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Info,
  FileText,
  Database,
  Calculator
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Progress } from '../components/ui/progress';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

// Tool Usage Goals (Planning & Modeling)
const TOOL_GOAL_TYPES = [
  {
    id: 'scenario_planning',
    label: 'Scenario Planning Target',
    description: 'Target a specific projected ROI from your scenario models',
    icon: TrendingUp,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    unit: '%',
    placeholder: '200',
    progressHint: 'Run comparisons on Dampening What-If',
    progressLink: '/dampening-compare',
    category: 'tool'
  },
  {
    id: 'comparison_count',
    label: 'Comparison Activity',
    description: 'Run a target number of scenario comparisons',
    icon: Activity,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    unit: 'comparisons',
    placeholder: '50',
    progressHint: 'Run comparisons on Dampening What-If',
    progressLink: '/dampening-compare',
    category: 'tool'
  },
  {
    id: 'factor_focus',
    label: 'Factor Exploration',
    description: 'Explore a specific factor in % of comparisons',
    icon: Target,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    unit: '%',
    placeholder: '60',
    progressHint: 'Run comparisons exploring this factor',
    progressLink: '/dampening-compare',
    category: 'tool'
  }
];

// Outcome Goals (Real-World Measurement)
const OUTCOME_GOAL_TYPES = [
  {
    id: 'lei_measure',
    label: 'LEI Measure Improvement',
    description: 'Track real improvement in a specific LEI measure from imported data',
    icon: GraduationCap,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    unit: '%',
    placeholder: '10',
    progressHint: 'Import updated LEI data',
    progressLink: '/data-management',
    category: 'outcome'
  },
  {
    id: 'kpi_improvement',
    label: 'KPI Improvement',
    description: 'Track real improvement in a business KPI from imported actuals',
    icon: LineChart,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    unit: '%',
    placeholder: '5',
    progressHint: 'Import actual KPI data',
    progressLink: '/data-management',
    category: 'outcome'
  }
];

// Combined for backward compatibility
const GOAL_TYPES = [...TOOL_GOAL_TYPES, ...OUTCOME_GOAL_TYPES];

const TIMEFRAMES = [
  { id: 'monthly', label: 'This Month' },
  { id: 'quarterly', label: 'This Quarter' },
  { id: 'yearly', label: 'This Year' },
  { id: 'custom', label: 'Custom Deadline' }
];

const FACTORS = [
  { id: 'adoption_speed', label: 'Adoption Speed' },
  { id: 'leadership_alignment', label: 'Leadership Alignment' },
  { id: 'operational_readiness', label: 'Operational Readiness' },
  { id: 'maturity', label: 'Maturity' },
  { id: 'headcount', label: 'Headcount/Scale' },
  { id: 'time_lag', label: 'Time Lag' }
];

const LEI_MEASURES = [
  { id: 'training_scope', name: 'Training Scope Index', pillar: 'P1 Access & Engagement' },
  { id: 'sme_training', name: 'SME Training Scope Index', pillar: 'P1 Access & Engagement' },
  { id: 'community_learning', name: 'Community Learning Index', pillar: 'P1 Access & Engagement' },
  { id: 'voluntary_engagement', name: 'Voluntary Learning Engagement Index', pillar: 'P1 Access & Engagement' },
  { id: 'tech_utilization', name: 'Learning Technology Utilization Index', pillar: 'P1 Access & Engagement' },
  { id: 'learning_culture', name: 'Organizational Learning Culture Index', pillar: 'P1 Access & Engagement' },
  { id: 'training_performance', name: 'Training Performance Index (TPI)', pillar: 'P2 Performance & Outcomes' },
  { id: 'nps', name: 'Net Promoter Score (NPS)', pillar: 'P2 Performance & Outcomes' },
  { id: 'skills_assessment', name: 'Skills Assessment Performance Index', pillar: 'P2 Performance & Outcomes' },
  { id: 'skills_coverage', name: 'Skills Coverage Index', pillar: 'P3 Capability & Readiness' },
  { id: 'certification_depth', name: 'Certification Depth Index', pillar: 'P3 Capability & Readiness' },
  { id: 'readiness_index', name: 'Readiness Index', pillar: 'P3 Capability & Readiness' },
  { id: 'cost_efficiency', name: 'Cost Efficiency Index', pillar: 'P4 Operational Efficiency' },
  { id: 'scheduling_velocity', name: 'Scheduling Velocity Index', pillar: 'P4 Operational Efficiency' },
  { id: 'kpi_alignment', name: 'KPI Alignment Strength Index', pillar: 'P5 Business Impact Alignment' }
];

const BUSINESS_KPIS = [
  { id: 'productivity', name: 'Productivity per FTE' },
  { id: 'quality', name: 'Quality / Error Reduction' },
  { id: 'safety', name: 'Safety Incident Reduction' },
  { id: 'csat', name: 'Customer Satisfaction (CSAT)' },
  { id: 'speed_market', name: 'Speed to Market / Responsiveness' },
  { id: 'scalability', name: 'Operational Scalability' },
  { id: 'cost_efficiency', name: 'Cost Efficiency / Opex' },
  { id: 'strategic_init', name: 'Strategic Initiative Completion' },
  { id: 'mobility', name: 'Internal Mobility / Bench Strength' },
  { id: 'engagement', name: 'Employee Engagement & Retention' }
];

// Pre-built goal templates for quick start
const GOAL_TEMPLATES = [
  // Tool Usage Templates
  {
    id: 'scenario_200',
    title: 'Achieve 200% Projected ROI',
    description: 'Target a 200% projected ROI from your scenario models',
    goal_type: 'scenario_planning',
    target_value: '200',
    timeframe: 'quarterly',
    icon: TrendingUp,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    category: 'tool'
  },
  {
    id: 'comparisons_50',
    title: 'Complete 50 Scenario Comparisons',
    description: 'Run comprehensive scenario analysis to optimize learning investment decisions',
    goal_type: 'comparison_count',
    target_value: '50',
    timeframe: 'quarterly',
    icon: Activity,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    category: 'tool'
  },
  {
    id: 'leadership_focus',
    title: 'Focus on Leadership Alignment',
    description: 'Ensure leadership alignment is explored in 60% of all comparisons',
    goal_type: 'factor_focus',
    target_value: '60',
    timeframe: 'quarterly',
    factor_name: 'leadership_alignment',
    icon: Target,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    category: 'tool'
  },
  // Outcome Templates
  {
    id: 'tpi_10',
    title: 'Improve Training Performance by 10%',
    description: 'Track real improvement in Training Performance Index from imported data',
    goal_type: 'lei_measure',
    target_value: '10',
    timeframe: 'quarterly',
    lei_measure_id: 'training_performance',
    baseline_value: '70',
    icon: GraduationCap,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    category: 'outcome'
  },
  {
    id: 'learning_culture',
    title: 'Boost Learning Culture Index by 15%',
    description: 'Track real improvement in organizational learning culture from imported data',
    goal_type: 'lei_measure',
    target_value: '15',
    timeframe: 'yearly',
    lei_measure_id: 'learning_culture',
    baseline_value: '55',
    icon: GraduationCap,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    category: 'outcome'
  },
  {
    id: 'productivity_5',
    title: 'Increase Actual Productivity by 5%',
    description: 'Track real productivity improvement from imported KPI actuals',
    goal_type: 'kpi_improvement',
    target_value: '5',
    timeframe: 'quarterly',
    kpi_id: 'productivity',
    icon: LineChart,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    category: 'outcome'
  },
  {
    id: 'safety_reduce',
    title: 'Reduce Safety Incidents by 3%',
    description: 'Track real safety incident reduction from imported KPI actuals',
    goal_type: 'kpi_improvement',
    target_value: '3',
    timeframe: 'yearly',
    kpi_id: 'safety',
    icon: LineChart,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    category: 'outcome'
  },
  {
    id: 'engagement_retention',
    title: 'Improve Employee Engagement by 4%',
    description: 'Track real engagement improvement from imported KPI actuals',
    goal_type: 'kpi_improvement',
    target_value: '4',
    timeframe: 'yearly',
    kpi_id: 'engagement',
    icon: LineChart,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    category: 'outcome'
  }
];

const GoalsPage = () => {
  const navigate = useNavigate();
  const { user, currency } = useApp();
  const { isSuperAdmin } = useAuth();

  const [goals, setGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [showTemplates, setShowTemplates] = useState(true);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('all');
  
  // New goal form state
  const [newGoal, setNewGoal] = useState({
    goal_type: 'scenario_planning',
    title: '',
    description: '',
    target_value: '',
    timeframe: 'quarterly',
    deadline: '',
    factor_name: 'leadership_alignment',
    lei_measure_id: 'training_scope',
    kpi_id: 'productivity',
    baseline_value: '',
    email_notifications: false
  });

  // Define loadGoals BEFORE the useEffect that uses it
  const loadGoals = useCallback(async () => {
    try {
      setIsLoading(true);
      const orgParam = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
      const response = await api.get(`/goals${orgParam}`);
      setGoals(response.data);
    } catch (error) {
      logger.error('Error loading goals:', error);
      toast.error('Failed to load goals');
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrg]);

  // Now the useEffect can safely reference loadGoals
  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get(`/rbac/organizations`).then(res => {
      setOrganizations(res.data.organizations || []);
    }).catch(() => {});
  }, [isSuperAdmin]);

  const createGoal = async () => {
    if (!newGoal.title.trim()) {
      toast.error('Please enter a goal title');
      return;
    }
    if (!newGoal.target_value || parseFloat(newGoal.target_value) <= 0) {
      toast.error('Please enter a valid target value');
      return;
    }

    try {
      setIsCreating(true);
      const payload = {
        ...newGoal,
        target_value: parseFloat(newGoal.target_value),
        baseline_value: newGoal.baseline_value ? parseFloat(newGoal.baseline_value) : null
      };
      await api.post(`/goals`, payload);
      toast.success('Goal created successfully!');
      setShowCreateDialog(false);
      resetNewGoal();
      loadGoals();
    } catch (error) {
      logger.error('Error creating goal:', error);
      toast.error('Failed to create goal');
    } finally {
      setIsCreating(false);
    }
  };

  const updateGoal = async () => {
    if (!editingGoal) return;

    try {
      await api.put(`/goals/${editingGoal.goal_id}`, {
        title: editingGoal.title,
        description: editingGoal.description,
        target_value: parseFloat(editingGoal.target_value),
        email_notifications: editingGoal.email_notifications
      });
      toast.success('Goal updated successfully!');
      setEditingGoal(null);
      loadGoals();
    } catch (error) {
      logger.error('Error updating goal:', error);
      toast.error('Failed to update goal');
    }
  };

  const deleteGoal = async (goalId) => {
    if (!confirm('Are you sure you want to delete this goal?')) return;

    try {
      await api.delete(`/goals/${goalId}`);
      toast.success('Goal deleted');
      loadGoals();
    } catch (error) {
      logger.error('Error deleting goal:', error);
      toast.error('Failed to delete goal');
    }
  };

  const markComplete = async (goalId) => {
    try {
      await api.post(`/goals/${goalId}/complete`);
      toast.success('Goal marked as complete! 🎉');
      loadGoals();
    } catch (error) {
      logger.error('Error completing goal:', error);
      toast.error('Failed to complete goal');
    }
  };

  const resetNewGoal = () => {
    setNewGoal({
      goal_type: 'scenario_planning',
      title: '',
      description: '',
      target_value: '',
      timeframe: 'quarterly',
      deadline: '',
      factor_name: 'leadership_alignment',
      lei_measure_id: 'training_scope',
      kpi_id: 'productivity',
      baseline_value: '',
      email_notifications: false
    });
  };

  const applyTemplate = (template) => {
    setNewGoal({
      goal_type: template.goal_type,
      title: template.title,
      description: template.description,
      target_value: template.target_value,
      timeframe: template.timeframe,
      deadline: '',
      factor_name: template.factor_name || 'leadership_alignment',
      lei_measure_id: template.lei_measure_id || 'training_scope',
      kpi_id: template.kpi_id || 'productivity',
      baseline_value: template.baseline_value || '',
      email_notifications: false
    });
    setShowTemplates(false);
    toast.success('Template applied! Customize and create your goal.');
  };

  const getGoalTypeInfo = (type) => {
    return GOAL_TYPES.find(t => t.id === type) || GOAL_TYPES[0];
  };

  const getTitlePlaceholder = (goalType) => {
    switch (goalType) {
      case 'scenario_planning':
        return 'e.g., Achieve 200% Projected ROI';
      case 'comparison_count':
        return 'e.g., Complete 50 scenario comparisons';
      case 'factor_focus':
        return 'e.g., Focus on Leadership Alignment';
      case 'lei_measure':
        return 'e.g., Improve Training Performance by 10%';
      case 'kpi_improvement':
        return 'e.g., Increase Productivity by 5%';
      default:
        return 'Enter a goal title';
    }
  };

  const formatDeadline = (deadline) => {
    return new Date(deadline).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getProgressColor = (progress, onTrack) => {
    if (progress >= 100) return 'bg-emerald-500';
    if (onTrack) return 'bg-blue-500';
    return 'bg-amber-500';
  };

  // Render a goal card with category styling
  const renderGoalCard = (goal, category) => {
    const typeInfo = getGoalTypeInfo(goal.goal_type);
    const progress = goal.progress || {};
    const borderColor = category === 'tool' ? 'border-l-purple-400' : 'border-l-rose-400';
    
    return (
      <Card key={goal.goal_id} className={`overflow-hidden border-l-4 ${borderColor}`}>
        <div className={`h-1 ${getProgressColor(progress.progress_percent, progress.on_track)}`} 
             style={{ width: `${progress.progress_percent || 0}%` }} />
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${typeInfo.bgColor}`}>
                <typeInfo.icon className={`h-5 w-5 ${typeInfo.color}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">{goal.title}</CardTitle>
                  <Badge variant="outline" className="text-xs">
                    {typeInfo.label}
                  </Badge>
                </div>
                {goal.description && (
                  <CardDescription className="text-xs mt-1">{goal.description}</CardDescription>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setEditingGoal(goal)}
                    >
                      <Edit className="h-4 w-4 text-slate-400" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit goal</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => deleteGoal(goal.goal_id)}
                    >
                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-500" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete goal</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">
                {progress.current_value?.toFixed(1) || 0} / {progress.target_value || goal.target_value} {typeInfo.unit}
              </span>
              <span className="font-medium text-slate-700">{progress.progress_percent?.toFixed(0) || 0}%</span>
            </div>
            <div className="relative">
              <Progress value={progress.progress_percent || 0} className="h-3" />
              <div className="absolute top-0 left-0 right-0 h-3 flex">
                {[25, 50, 75].map(milestone => (
                  <div
                    key={milestone}
                    className="absolute h-full w-0.5 bg-slate-300"
                    style={{ left: `${milestone}%` }}
                  />
                ))}
              </div>
            </div>
            <div className="flex justify-between text-xs">
              {progress.milestones?.map(m => (
                <span
                  key={m.percent}
                  className={`${m.reached ? 'text-emerald-600 font-medium' : 'text-slate-400'}`}
                >
                  {m.percent}%
                  {m.reached && <CheckCircle2 className="h-3 w-3 inline ml-0.5" />}
                </span>
              )) || (
                <>
                  <span className="text-slate-400">25%</span>
                  <span className="text-slate-400">50%</span>
                  <span className="text-slate-400">75%</span>
                  <span className="text-slate-400">100%</span>
                </>
              )}
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 text-slate-500">
                <Clock className="h-4 w-4" />
                <span>{progress.days_remaining || 0} days left</span>
              </div>
              <div className="flex items-center gap-1 text-slate-500">
                <Calendar className="h-4 w-4" />
                <span>{goal.deadline ? formatDeadline(goal.deadline) : 'No deadline'}</span>
              </div>
            </div>
            {progress.on_track ? (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                On Track
              </Badge>
            ) : progress.days_remaining > 0 ? (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                <AlertTriangle className="h-3 w-3 mr-1" />
                At Risk
              </Badge>
            ) : (
              <Badge className="bg-rose-100 text-rose-700 border-rose-200">
                Overdue
              </Badge>
            )}
          </div>

          {/* How to Progress Hint */}
          {(progress.progress_percent || 0) < 50 && typeInfo.progressHint && (
            <div className={`flex items-center justify-between p-2 rounded-lg text-xs ${
              category === 'tool' ? 'bg-purple-50' : 'bg-rose-50'
            }`}>
              <div className="flex items-center gap-2 text-slate-600">
                <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                <span>To make progress: {typeInfo.progressHint}</span>
              </div>
              <button 
                data-testid="goal-progress-link"
                onClick={() => navigate(typeInfo.progressLink)}
                className={`font-medium flex items-center gap-1 ${
                  category === 'tool' ? 'text-purple-600 hover:text-purple-700' : 'text-rose-600 hover:text-rose-700'
                }`}
              >
                Go <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Complete Button */}
          {(progress.progress_percent || 0) >= 100 && (
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => markComplete(goal.goal_id)}
            >
              <Trophy className="h-4 w-4 mr-2" />
              Mark as Complete
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  const activeGoals = goals.filter(g => !g.completed);
  const completedGoals = goals.filter(g => g.completed);
  const atRiskGoals = activeGoals.filter(g => g.progress && !g.progress.on_track);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading goals...</p>
        </div>
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  const goalsHeroActions = (
    <div className="flex items-center gap-2">
      {isSuperAdmin && organizations.length > 0 && (
        <select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
          className="text-sm rounded-lg px-3 py-1.5 bg-white border border-slate-200 text-slate-800 border-0 focus:ring-2 focus:ring-white/50 min-w-[160px]"
          data-testid="goals-org-filter"
        >
          <option value="all">All Organizations</option>
          {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
      <Button onClick={() => setShowCreateDialog(true)} className="bg-white text-amber-700 hover:bg-slate-50">
        <Plus className="h-4 w-4 mr-2" /> Create Goal
      </Button>
    </div>
  );
  const goalsHeroStats = [
    { label: 'Total Goals', value: goals.length, testId: 'goals-stat-total' },
    { label: 'Active', value: activeGoals.length, testId: 'goals-stat-active' },
    { label: 'Completed', value: completedGoals.length, testId: 'goals-stat-completed' },
    { label: 'At Risk', value: atRiskGoals.length, testId: 'goals-stat-atrisk' },
  ];

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Goals & Progress"
        description="Set targets and track your learning analytics progress. Monitor ROI goals, comparison activities, and performance metrics so you know which interventions actually moved the needle."
        theme={__pageTheme}
        icon={Target}
        actions={goalsHeroActions}
        stats={goalsHeroStats}
        testId="goals-hero"
      />
    )}>
    <div className="space-y-6" data-testid="goals-page">

      {/* How Goal Tracking Works Section */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <button
            onClick={() => setShowHowItWorks(!showHowItWorks)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-base">How Goal Tracking Works</CardTitle>
            </div>
            {showHowItWorks ? (
              <ChevronUp className="h-5 w-5 text-slate-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate-400" />
            )}
          </button>
          {!showHowItWorks && (
            <CardDescription className="mt-1 ml-7">
              Click to learn how your goals are measured and updated automatically
            </CardDescription>
          )}
        </CardHeader>
        
        {showHowItWorks && (
          <CardContent className="pt-2">
            <div className="space-y-4">
              {/* Overview */}
              <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-purple-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Automatic Progress Tracking</p>
                    <p className="text-sm text-slate-600 mt-1">
                      Your goal progress is calculated automatically based on your activity in the app. 
                      Each goal type pulls data from different sources — the more you use the relevant features, 
                      the more your progress updates.
                    </p>
                  </div>
                </div>
              </div>

              {/* Goal Types Explanation - Two Categories */}
              <div className="space-y-4">
                {/* Tool Usage Goals */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Calculator className="h-4 w-4 text-purple-600" />
                    <span className="font-medium text-slate-700">Tool Usage Goals</span>
                    <Badge variant="outline" className="text-xs">Planning & Modeling</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Scenario Planning */}
                    <div className="p-3 border border-purple-100 rounded-lg bg-purple-50/30">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-emerald-600" />
                        <span className="text-sm font-medium text-slate-700">Scenario Planning</span>
                      </div>
                      <p className="text-xs text-slate-600 mb-2">
                        Track projected ROI from scenario models
                      </p>
                      <div className="text-xs text-slate-500">
                        <span className="font-medium">Source:</span> Dampening What-If
                      </div>
                    </div>

                    {/* Comparison Count */}
                    <div className="p-3 border border-purple-100 rounded-lg bg-purple-50/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium text-slate-700">Comparison Activity</span>
                      </div>
                      <p className="text-xs text-slate-600 mb-2">
                        Count of scenario comparisons run
                      </p>
                      <div className="text-xs text-slate-500">
                        <span className="font-medium">Source:</span> Dampening What-If
                      </div>
                    </div>

                    {/* Factor Focus */}
                    <div className="p-3 border border-purple-100 rounded-lg bg-purple-50/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-slate-700">Factor Exploration</span>
                      </div>
                      <p className="text-xs text-slate-600 mb-2">
                        % of comparisons using a specific factor
                      </p>
                      <div className="text-xs text-slate-500">
                        <span className="font-medium">Source:</span> Dampening What-If
                      </div>
                    </div>
                  </div>
                </div>

                {/* Outcome Goals */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="h-4 w-4 text-rose-600" />
                    <span className="font-medium text-slate-700">Outcome Goals</span>
                    <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-200">Real-World Measurement</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* LEI Measure */}
                    <div className="p-3 border border-rose-100 rounded-lg bg-rose-50/30">
                      <div className="flex items-center gap-2 mb-2">
                        <GraduationCap className="h-4 w-4 text-amber-600" />
                        <span className="text-sm font-medium text-slate-700">LEI Measure Improvement</span>
                      </div>
                      <p className="text-xs text-slate-600 mb-2">
                        Track real improvement in LEI measures from periodic data imports
                      </p>
                      <div className="text-xs text-slate-500 space-y-1">
                        <div><span className="font-medium">Source:</span> Data Management → LEI Import</div>
                        <div><span className="font-medium">Formula:</span> ((Current - Baseline) / Baseline) × 100</div>
                      </div>
                    </div>

                    {/* KPI Improvement */}
                    <div className="p-3 border border-rose-100 rounded-lg bg-rose-50/30">
                      <div className="flex items-center gap-2 mb-2">
                        <LineChart className="h-4 w-4 text-rose-600" />
                        <span className="text-sm font-medium text-slate-700">KPI Improvement</span>
                      </div>
                      <p className="text-xs text-slate-600 mb-2">
                        Track real business KPI improvement from periodic data imports
                      </p>
                      <div className="text-xs text-slate-500 space-y-1">
                        <div><span className="font-medium">Source:</span> Data Management → KPI Import</div>
                        <div><span className="font-medium">Formula:</span> ((Latest value - First value) / First value) × 100</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Tips */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  Tips for Making Progress
                </p>
                <ul className="text-xs text-slate-600 space-y-1.5 ml-6">
                  <li>• <strong>Tool Usage Goals:</strong> Run scenarios on Dampening What-If to track modeling activity</li>
                  <li>• <strong>LEI Measure Goals:</strong> Import updated LEI data via Data Management → LEI Import</li>
                  <li>• <strong>KPI Goals:</strong> Import actual KPI data via Data Management → KPI Import</li>
                  <li>• Progress updates happen automatically when you import new data!</li>
                </ul>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Set Target for Gap Analysis - Quick Setup */}
      <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50/50 to-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-100">
                <Target className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Set Targets for Gap Analysis</CardTitle>
                <CardDescription>Create projections and track actual performance against targets</CardDescription>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="border-teal-300 text-teal-700 hover:bg-teal-50"
              onClick={() => navigate('/gap-analysis')}
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              View Gap Analysis
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Step 1: Run Projections */}
            <div className="relative p-4 bg-white border border-purple-100 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="absolute -top-3 left-4">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold">1</span>
              </div>
              <div className="pt-2">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="h-5 w-5 text-purple-600" />
                  <h4 className="font-medium text-slate-800">Set Projections</h4>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Run ROI calculations to define your target KPI improvements
                </p>
                <Button 
                  size="sm" 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={() => navigate('/solution-finder')}
                >
                  Solution Finder
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>

            {/* Step 2: Import Actuals */}
            <div className="relative p-4 bg-white border border-rose-100 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="absolute -top-3 left-4">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-rose-600 text-white text-xs font-bold">2</span>
              </div>
              <div className="pt-2">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="h-5 w-5 text-rose-600" />
                  <h4 className="font-medium text-slate-800">Import Actuals</h4>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Upload real KPI performance data to compare against targets
                </p>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="w-full border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => navigate('/data-management')}
                >
                  Import KPI Data
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>

            {/* Step 3: Track Gap */}
            <div className="relative p-4 bg-white border border-teal-100 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="absolute -top-3 left-4">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-600 text-white text-xs font-bold">3</span>
              </div>
              <div className="pt-2">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-5 w-5 text-teal-600" />
                  <h4 className="font-medium text-slate-800">Track Progress</h4>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Monitor gap between targets and actuals with alerts
                </p>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="w-full border-teal-300 text-teal-700 hover:bg-teal-50"
                  onClick={() => navigate('/gap-analysis')}
                >
                  Gap Analysis
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Tips */}
          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
            <div className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-600">
                <span className="font-medium text-slate-700">Pro Tip:</span> Gap Analysis automatically compares your projected ROI targets against imported actuals. 
                Set up alerts to be notified when performance deviates from targets by more than a threshold you define.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Goals - Segmented by Category */}
      <div className="space-y-6">
        <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
          <Activity className="h-5 w-5 text-purple-600" />
          Active Goals
        </h2>

        {activeGoals.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Target className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Active Goals</h3>
              <p className="text-slate-500 mb-4">Set your first goal to start tracking progress</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Goal
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Tool Usage Goals Section */}
            {activeGoals.filter(g => ['scenario_planning', 'roi_target', 'comparison_count', 'factor_focus'].includes(g.goal_type)).length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 pb-2 border-b border-purple-100">
                  <div className="p-1.5 rounded-lg bg-purple-100">
                    <Calculator className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-800">Tool Usage Goals</h3>
                    <p className="text-xs text-slate-500">Planning & Modeling Activity</p>
                  </div>
                  <Badge className="ml-auto bg-purple-100 text-purple-700 border-purple-200">
                    {activeGoals.filter(g => ['scenario_planning', 'roi_target', 'comparison_count', 'factor_focus'].includes(g.goal_type)).length} active
                  </Badge>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {activeGoals
                    .filter(g => ['scenario_planning', 'roi_target', 'comparison_count', 'factor_focus'].includes(g.goal_type))
                    .map(goal => renderGoalCard(goal, 'tool'))}
                </div>
              </div>
            )}

            {/* Outcome Goals Section */}
            {activeGoals.filter(g => ['lei_measure', 'kpi_improvement'].includes(g.goal_type)).length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 pb-2 border-b border-rose-100">
                  <div className="p-1.5 rounded-lg bg-rose-100">
                    <BarChart3 className="h-4 w-4 text-rose-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-800">Outcome Goals</h3>
                    <p className="text-xs text-slate-500">Real-World Measurement</p>
                  </div>
                  <Badge className="ml-auto bg-rose-100 text-rose-700 border-rose-200">
                    {activeGoals.filter(g => ['lei_measure', 'kpi_improvement'].includes(g.goal_type)).length} active
                  </Badge>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {activeGoals
                    .filter(g => ['lei_measure', 'kpi_improvement'].includes(g.goal_type))
                    .map(goal => renderGoalCard(goal, 'outcome'))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Completed Goals */}
      {completedGoals.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-emerald-600" />
            Completed Goals
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {completedGoals.map(goal => {
              const typeInfo = getGoalTypeInfo(goal.goal_type);
              
              return (
                <Card key={goal.goal_id} className="bg-emerald-50/30 border-emerald-100">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-emerald-100">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-700">{goal.title}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Completed {goal.completed_at ? formatDeadline(goal.completed_at) : 'recently'}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => deleteGoal(goal.goal_id)}
                      >
                        <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Create Goal Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (open) {
          setShowTemplates(true);
          resetNewGoal();
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-600" />
              Create New Goal
            </DialogTitle>
            <DialogDescription>
              Set a target and track your progress over time
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            {/* Quick Start Templates */}
            <div className="space-y-2">
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  <Label className="cursor-pointer font-medium">Quick Start Templates</Label>
                  <Badge variant="secondary" className="text-xs">
                    {GOAL_TEMPLATES.length} templates
                  </Badge>
                </div>
                {showTemplates ? (
                  <ChevronUp className="h-4 w-4 text-slate-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                )}
              </button>
              
              {showTemplates && (
                <div className="grid grid-cols-1 gap-2 mt-2">
                  {GOAL_TEMPLATES.map(template => (
                    <button
                      key={template.id}
                      data-testid={`template-${template.id}`}
                      onClick={() => applyTemplate(template)}
                      className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-purple-300 hover:bg-purple-50/50 transition-all text-left group"
                    >
                      <div className={`p-2 rounded-lg ${template.bgColor} shrink-0`}>
                        <template.icon className={`h-4 w-4 ${template.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-700 truncate">{template.title}</p>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {GOAL_TYPES.find(t => t.id === template.goal_type)?.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{template.description}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-purple-500 shrink-0 mt-1" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 pt-4">
              <p className="text-xs text-slate-500 mb-4">Or create a custom goal:</p>
            </div>

            {/* Goal Type Selection - Two Categories with Clear Separation */}
            <div className="space-y-6">
              {/* Tool Usage Goals */}
              <div className="p-4 rounded-xl border-2 border-purple-100 bg-gradient-to-br from-purple-50/50 to-indigo-50/30">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <Calculator className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-800">Tool Usage Goals</h3>
                      <Badge className="bg-purple-100 text-purple-700 border-purple-200">Planning & Modeling</Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">
                      Track your scenario planning activity and analysis depth
                    </p>
                  </div>
                </div>
                
                <div className="bg-white border border-slate-200 rounded-lg p-3 mb-3 text-xs text-slate-600">
                  <strong>When to use:</strong> Set these goals to ensure you're thoroughly exploring scenarios 
                  before making investment decisions. They track your <em>planning process</em>, not actual outcomes.
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  {TOOL_GOAL_TYPES.map(type => (
                    <button
                      key={type.id}
                      data-testid={`goal-type-${type.id}`}
                      onClick={() => setNewGoal({ ...newGoal, goal_type: type.id })}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        newGoal.goal_type === type.id
                          ? 'border-purple-500 bg-white shadow-sm'
                          : 'border-transparent bg-white/50 hover:bg-white hover:border-purple-200'
                      }`}
                    >
                      <type.icon className={`h-5 w-5 mb-2 ${type.color}`} />
                      <p className="text-sm font-medium text-slate-700">{type.label}</p>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Outcome Goals */}
              <div className="p-4 rounded-xl border-2 border-rose-100 bg-gradient-to-br from-rose-50/50 to-amber-50/30">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-rose-100">
                    <BarChart3 className="h-5 w-5 text-rose-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-800">Outcome Goals</h3>
                      <Badge className="bg-rose-100 text-rose-700 border-rose-200">Real-World Measurement</Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">
                      Track actual improvement from your imported data
                    </p>
                  </div>
                </div>
                
                <div className="bg-white border border-slate-200 rounded-lg p-3 mb-3 text-xs text-slate-600">
                  <strong>When to use:</strong> Set these goals to measure <em>real business impact</em>. 
                  Progress is tracked by importing actual LEI scores and KPI metrics over time via Data Management.
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {OUTCOME_GOAL_TYPES.map(type => (
                    <button
                      key={type.id}
                      data-testid={`goal-type-${type.id}`}
                      onClick={() => setNewGoal({ ...newGoal, goal_type: type.id })}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        newGoal.goal_type === type.id
                          ? 'border-rose-500 bg-white shadow-sm'
                          : 'border-transparent bg-white/50 hover:bg-white hover:border-rose-200'
                      }`}
                    >
                      <type.icon className={`h-5 w-5 mb-2 ${type.color}`} />
                      <p className="text-sm font-medium text-slate-700">{type.label}</p>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="goal-title">Goal Title</Label>
              <Input
                id="goal-title"
                data-testid="goal-title-input"
                placeholder={getTitlePlaceholder(newGoal.goal_type)}
                value={newGoal.title}
                onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="goal-desc">Description (optional)</Label>
              <Textarea
                id="goal-desc"
                placeholder="What do you want to achieve?"
                value={newGoal.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                rows={2}
              />
            </div>

            {/* Target Value */}
            <div className="space-y-2">
              <Label htmlFor="goal-target">
                Target Value ({getGoalTypeInfo(newGoal.goal_type).unit})
              </Label>
              <Input
                id="goal-target"
                type="number"
                placeholder={getGoalTypeInfo(newGoal.goal_type).placeholder}
                value={newGoal.target_value}
                onChange={(e) => setNewGoal({ ...newGoal, target_value: e.target.value })}
              />
            </div>

            {/* Factor Selection (for factor_focus type) */}
            {newGoal.goal_type === 'factor_focus' && (
              <div className="space-y-2">
                <Label>Factor to Focus On</Label>
                <Select
                  value={newGoal.factor_name}
                  onValueChange={(v) => setNewGoal({ ...newGoal, factor_name: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FACTORS.map(factor => (
                      <SelectItem key={factor.id} value={factor.id}>
                        {factor.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* LEI Measure Selection (for lei_measure type) */}
            {newGoal.goal_type === 'lei_measure' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>LEI Measure to Improve</Label>
                  <Select
                    value={newGoal.lei_measure_id}
                    onValueChange={(v) => setNewGoal({ ...newGoal, lei_measure_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a LEI measure" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEI_MEASURES.map(measure => (
                        <SelectItem key={measure.id} value={measure.id}>
                          <div className="flex flex-col">
                            <span>{measure.name}</span>
                            <span className="text-xs text-slate-400">{measure.pillar}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="baseline-value">Current Baseline Score (%)</Label>
                  <Input
                    id="baseline-value"
                    type="number"
                    placeholder="e.g., 65"
                    value={newGoal.baseline_value}
                    onChange={(e) => setNewGoal({ ...newGoal, baseline_value: e.target.value })}
                  />
                  <p className="text-xs text-slate-500">
                    Enter your current score to track improvement percentage
                  </p>
                </div>
              </div>
            )}

            {/* KPI Selection (for kpi_improvement type) */}
            {newGoal.goal_type === 'kpi_improvement' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Business KPI to Improve</Label>
                  <Select
                    value={newGoal.kpi_id}
                    onValueChange={(v) => setNewGoal({ ...newGoal, kpi_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a KPI" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_KPIS.map(kpi => (
                        <SelectItem key={kpi.id} value={kpi.id}>
                          {kpi.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                  Track the projected uplift percentage from your latest ROI analysis. 
                  Run calculations to see progress toward this goal.
                </p>
              </div>
            )}

            {/* Timeframe */}
            <div className="space-y-2">
              <Label>Timeframe</Label>
              <Select
                value={newGoal.timeframe}
                onValueChange={(v) => setNewGoal({ ...newGoal, timeframe: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEFRAMES.map(tf => (
                    <SelectItem key={tf.id} value={tf.id}>
                      {tf.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Deadline */}
            {newGoal.timeframe === 'custom' && (
              <div className="space-y-2">
                <Label htmlFor="goal-deadline">Custom Deadline</Label>
                <Input
                  id="goal-deadline"
                  type="date"
                  value={newGoal.deadline}
                  onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
                />
              </div>
            )}

            {/* Email Notifications */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Email Notifications</p>
                <p className="text-xs text-slate-500">Get notified when you hit milestones</p>
              </div>
              <Switch
                checked={newGoal.email_notifications}
                onCheckedChange={(checked) => setNewGoal({ ...newGoal, email_notifications: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createGoal} disabled={isCreating}>
              {isCreating ? (
                <>
                  <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Goal
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Goal Dialog */}
      <Dialog open={!!editingGoal} onOpenChange={(open) => !open && setEditingGoal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Goal</DialogTitle>
          </DialogHeader>
          {editingGoal && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editingGoal.title}
                  onChange={(e) => setEditingGoal({ ...editingGoal, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingGoal.description}
                  onChange={(e) => setEditingGoal({ ...editingGoal, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Target Value</Label>
                <Input
                  type="number"
                  value={editingGoal.target_value}
                  onChange={(e) => setEditingGoal({ ...editingGoal, target_value: e.target.value })}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Email Notifications</p>
                </div>
                <Switch
                  checked={editingGoal.email_notifications}
                  onCheckedChange={(checked) => setEditingGoal({ ...editingGoal, email_notifications: checked })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingGoal(null)}>
              Cancel
            </Button>
            <Button onClick={updateGoal}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageShell>
  );
};

export default GoalsPage;
